import { useState } from "react";
import { useInView } from "../../../hooks/useInView";
import { shopProducts, shopCategories, type ShopProduct } from "../../../mocks/shop";

interface CartItem {
  product: ShopProduct;
  qty: number;
}

interface ToastMsg {
  id: number;
  text: string;
}

interface ProductCardProps {
  product: ShopProduct;
  onAdd: (p: ShopProduct) => void;
  index: number;
  inView: boolean;
}

function ProductCard({ product, onAdd, index, inView }: ProductCardProps) {
  return (
    <div
      className={`group bg-white border border-gray-200 overflow-hidden hover:border-orange-300 hover:-translate-y-1 transition-all duration-300 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}
      style={{ animationDelay: `${100 + index * 80}ms` }}
    >
      {/* Image */}
      <div className="relative w-full h-56 overflow-hidden bg-gray-50">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
          style={{ transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
        {product.badge && (
          <div className={`absolute top-3 left-3 text-white font-condensed font-bold text-xs uppercase tracking-widest px-2 py-1 ${product.badge === "Limited" ? "bg-gray-900" : product.badge === "Bestseller" ? "bg-orange-500" : "bg-green-600"}`}>
            {product.badge}
          </div>
        )}
        {/* Quick view overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-end justify-center pb-4">
          <span className="text-white font-condensed font-bold text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 bg-black/50 px-3 py-1">
            View Details
          </span>
        </div>
        {/* Orange bottom strip */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
      </div>

      {/* Info */}
      <div className="p-4">
        <p className="text-gray-400 font-condensed font-semibold text-xs uppercase tracking-widest mb-1">
          {product.category}
        </p>
        <h4 className="font-condensed font-black text-gray-900 text-lg uppercase tracking-tight mb-1 group-hover:text-orange-500 transition-colors duration-200">
          {product.name}
        </h4>
        <p className="text-gray-600 text-xs mb-4 leading-relaxed line-clamp-2">
          {product.description}
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="font-condensed font-black text-gray-900 text-xl">
            ${product.price}
          </span>
          <button
            onClick={() => onAdd(product)}
            className="inline-flex items-center gap-1.5 bg-gray-900 hover:bg-orange-500 text-white font-condensed font-bold text-xs uppercase tracking-widest px-4 py-2 transition-all duration-200 cursor-pointer whitespace-nowrap group/btn"
          >
            <i className="ri-shopping-bag-line text-sm" />
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductGrid() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const { ref, inView } = useInView();

  const filtered = activeCategory === "All"
    ? shopProducts
    : shopProducts.filter((p) => p.category === activeCategory);

  const totalItems = cart.reduce((sum, item) => sum + item.qty, 0);
  const totalPrice = cart.reduce((sum, item) => sum + item.product.price * item.qty, 0);

  const addToCart = (product: ShopProduct) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) => i.product.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { product, qty: 1 }];
    });
    const id = Date.now();
    setToasts((prev) => [...prev, { id, text: `${product.name} added!` }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  };

  const removeFromCart = (productId: number) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  };

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-white py-16 md:py-20 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-dot-grid-light pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header Row */}
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}>
          <div>
            <div className="inline-block border border-gray-300 text-gray-500 font-condensed font-semibold text-xs uppercase tracking-widest px-3 py-1 mb-3">
              Official Merch
            </div>
            <h2 className="font-condensed font-black text-gray-900 text-4xl md:text-5xl uppercase tracking-tight">
              All Products
            </h2>
            <div className={`mt-3 h-0.5 bg-orange-500 w-16 anim-hidden ${inView ? "line-grow delay-200" : ""}`} />
          </div>

          {/* Cart Button */}
          <button
            onClick={() => setCartOpen(true)}
            className="relative inline-flex items-center gap-2 bg-zinc-900 hover:bg-orange-500 text-white font-condensed font-bold text-sm uppercase tracking-widest px-5 py-2.5 transition-all duration-200 cursor-pointer whitespace-nowrap group"
          >
            <i className="ri-shopping-bag-line" />
            Cart
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-orange-500 group-hover:bg-zinc-900 text-white text-xs font-bold flex items-center justify-center transition-colors duration-200">
                {totalItems}
              </span>
            )}
          </button>
        </div>

        {/* Category Filters */}
        <div className={`flex flex-wrap gap-2 mb-10 anim-hidden ${inView ? "animate-fade-in-up delay-150" : ""}`}>
          {shopCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-condensed font-bold text-sm uppercase tracking-widest px-4 py-2 transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                activeCategory === cat
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-900 hover:text-gray-900"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((product, i) => (
            <ProductCard key={product.id} product={product} onAdd={addToCart} index={i} inView={inView} />
          ))}
        </div>
      </div>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setCartOpen(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative bg-white w-full max-w-md h-full flex flex-col overflow-hidden animate-fade-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cart Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="font-condensed font-black text-gray-900 text-xl uppercase tracking-tight">
                Your Cart ({totalItems})
              </h3>
              <button
                onClick={() => setCartOpen(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-700" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100">
                    <i className="ri-shopping-bag-line text-3xl text-gray-400" />
                  </div>
                  <p className="text-gray-400 font-condensed uppercase tracking-widest text-sm">
                    Your cart is empty
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {cart.map((item) => (
                    <div key={item.product.id} className="flex items-center gap-4 border-b border-gray-100 pb-4">
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden bg-gray-100">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-condensed font-bold text-gray-900 text-sm uppercase tracking-tight truncate">
                          {item.product.name}
                        </p>
                        <p className="text-gray-500 text-xs">Qty: {item.qty}</p>
                        <p className="font-condensed font-bold text-orange-500 text-sm mt-0.5">
                          ${item.product.price * item.qty}
                        </p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-delete-bin-line text-gray-400 text-sm" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-condensed font-bold text-gray-700 text-sm uppercase tracking-wide">Total</span>
                  <span className="font-condensed font-black text-gray-900 text-xl">${totalPrice}</span>
                </div>
                <button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-condensed font-bold text-sm uppercase tracking-widest py-3.5 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Checkout
                </button>
                <p className="text-gray-400 text-xs text-center mt-3">
                  Secure checkout — PWA official store
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="bg-zinc-900 text-white font-condensed font-semibold text-sm uppercase tracking-wide px-4 py-3 flex items-center gap-2 border-l-2 border-orange-500 animate-fade-in-right"
          >
            <i className="ri-checkbox-circle-fill text-orange-400" />
            {t.text}
          </div>
        ))}
      </div>
    </section>
  );
}
